/** @jsx jsx */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import cn from 'classnames';
import { Query } from 'react-apollo';
import { jsx } from '@emotion/core';

import GridFlaskController from '../../controllers/GridFlaskController';
import GridFlask from '../../components/GridFlask';
import PageContainer from '../../components/PageContainer';
import ActionsHeader from '../../components/ActionsHeader';
import BlockingStatus from '../../components/BlockingStatus';
import Col from '../../components/Col';
import { REP_OPTIONS as RepFilterOptions } from '../../components/actions/RepFilterAction';
import ActionsOrganizer from './components/ActionsOrganizer';
import userSettingsQuery from '../../graphql/queries/userSettings.query';
import { getDefaultUserSettings, getUserSettings } from '../../utils'
import formatters from './formatters';
import { isAccountRep } from '../../utils/isAccountRep';
import ArrayUtils from '../../utils/arrayUtils'
import Filters from '../../filters/Filters'
import { MODULES_NAMES } from '../modulesList'
import { MobileView, useMobileMediaQuery } from '../../config/mediaQuery';
import ModuleMenuBar from '../../components/ModuleMenuBar';
import useMenuHandler from '../../hooks/useMenuHandler';

type Props = {
  isFullPage: boolean,
  showGroupPanel?: boolean,
  active: boolean,
};

const Invoices = (props: Props) => {
  const moduleName = MODULES_NAMES.INVOICES;

  const {
    isFullPage = true,
    showGroupPanel = false,
    active
  } = props;

  const menuRef = useRef();
  const menuButtonRef = useRef();

  const isMobile = useMobileMediaQuery();
  const [displayMenu, setDisplayMenu] = useState(false);
  
  // State to track the displayed view name for UI updates
  // - undefined: initial state, will sync from query on first load
  // - null: user selected "Default View"
  // - string: user selected a named view
  const [displayedViewName, setDisplayedViewName] = useState(undefined);

  const changeDisplayMenu = (status) => {
    setDisplayMenu(status);
  };

  const handler = useCallback((e) => {
    if (menuRef.current
      && menuButtonRef.current
      && !menuRef.current.contains(e.target)
      && !menuButtonRef.current.contains(e.target)
    ) {
      setDisplayMenu(value => {
        if (value) {
          return false;
        }
        return value;
      });
    }
  }, []);
  
  useMenuHandler(handler, active);

  useEffect(() => {
    let handler = (e) => {
      if (menuRef.current
        && menuButtonRef.current
        && !menuRef.current.contains(e.target)
        && !menuButtonRef.current.contains(e.target)
      ) {
        setDisplayMenu(false);
      }
    };
    isMobile && document.addEventListener("touchstart", handler);

    return () => {
      isMobile && document.removeEventListener("touchstart", handler);
    }
  }, []);

  return (
    <PageContainer
      className={cn({
        'page--is-full-page': isFullPage,
        'page--is-nested': !isFullPage,
        'page--with-group-panel': showGroupPanel,
      })}
      isFullPage={isFullPage}
      active={props.active}
    >
      <Query
        query={userSettingsQuery}
        variables={{
          moduleName: moduleName,
        }}
      >
        {({ data: dataGrid, loading }: any) => {
          // we need to wait until the Query is loaded otherwise
          // we'll have to refetch the grid multiple times (first on
          // page load and then when this query is loaded)
          if (loading || !dataGrid)
            return (
              <BlockingStatus
                excludeHeader
                active
                container={document.body}
                text="Loading..."
              />
            );

          const userSettings = getUserSettings(dataGrid, {
            defaultFilters: settings => {
              if (settings && isAccountRep(settings.accountType)) {
                const [defaultOption] = RepFilterOptions;

                return {
                  Rep: {
                    // This could look to something like this:
                    //   Rep: {
                    //     AcctR: {
                    //       eq: 'CO01',
                    //     },
                    //   },
                    [defaultOption.value]: {
                      eq: settings.repCode,
                    },
                  },
                };
              }

              return {};
            },

          });

          const defaultUserSettings = getDefaultUserSettings(dataGrid)
          
          // Get view name from query for initial sync
          var queryViewName = dataGrid && dataGrid.userSettings && dataGrid.userSettings.viewName
            ? dataGrid.userSettings.viewName
            : null;
          
          // Only sync displayedViewName from query on initial load (when displayedViewName is undefined)
          // After initial load, displayedViewName is managed entirely by user selection
          if (displayedViewName === undefined) {
            // Use setTimeout to avoid setState during render
            setTimeout(function() { setDisplayedViewName(queryViewName); }, 0);
          }
          
          // Determine what to show in the UI:
          // - If displayedViewName is undefined (initial load), use queryViewName
          // - Otherwise use displayedViewName (which could be null for Default View)
          var currentViewNameForUI = displayedViewName === undefined ? queryViewName : displayedViewName;

          // Handler for when a view is loaded - just updates the UI display name
          // The actual view settings are applied by onApplyViewSettings in ActionsHeader
          var handleViewLoaded = function(viewName) {
            setDisplayedViewName(viewName);
          };

          return (
            <GridFlaskController
              filterExternal={Filters.InvoiceFilterExternal}
              filterInternal={Filters.InvoiceFilterInternal}
              entity={moduleName}
              flattenFunction={ArrayUtils.flattenInvoiceCollection}
              loading={loading}
              defaultSorting={defaultUserSettings.defaultSorting}
              defaultColumns={defaultUserSettings.defaultColumns}
              defaultGroupPanels={defaultUserSettings.defaultGroupPanels}
              isMobile={isMobile}
              {...userSettings}
            >
              {({
                collection,
                columnsMapping,
                currentItem,
                defaultColumns,
                filters,
                frozenColumns,
                grid,
                groups,
                hasAllColumnsHidden,
                onRowSelected,
                onGridInitialized,
                onGroupPanelChanged,
                onColSorted,
                onColResized,
                onColDragged,
                onColFreezed,
                onRowFreezed,
                onGroupCollapsedChanged,
                onClearSelection,
                onColumnsMappingChange,
                onViewSaved,
                onFiltersChanged,
                onInternalGridFiltersChanged,
                onViewReset,
                onApplyViewSettings,
                onExporting,
                onExported,
                hasDataToDisplay,
                refetch,
                isLoading,
                isCollapseLoading,
                setCollapseLoading,
                status,
                saveStatus,
                selectedItems,
                userViewState,
                playResetAnimation,
                handleResetAnimation,
                setFlexGridFilter,
                clearInternalFilters,
                onCurrentItemChanged,
                toggleMultiSort,
                multiColumnSort,
                exportProgress,
                setExportProgress,
                getGridFilterDefinition,
              }) => {
                return (
                  <React.Fragment>
                    {/* Show loading indicator during collapse/expand operations */}
                    {isCollapseLoading && (
                      <BlockingStatus
                        excludeHeader
                        active
                        container={document.body}
                        text="Processing..."
                      />
                    )}
                    <ActionsHeader
                      grid={grid}
                      collection={collection}
                      defaultColumnsMapping={defaultColumns}
                      columnsMapping={columnsMapping}
                      selectedItems={selectedItems}
                      moduleName={moduleName}
                      groups={groups}
                      saveStatus={saveStatus}
                      status={status}
                      loading={isLoading || status !== 'loaded'}
                      onViewSaved={onViewSaved}
                      onViewReset={onViewReset}
                      onApplyViewSettings={onApplyViewSettings}
                      onColumnsMappingChange={onColumnsMappingChange}
                      onExporting={onExporting}
                      onExported={onExported}
                      userViewState={userViewState}
                      refetch={refetch}
                      hasAllColumnsHidden={hasAllColumnsHidden}
                      hasDataToDisplay={hasDataToDisplay}
                      frozenColumns={frozenColumns}
                      currentItem={currentItem}
                      defaultSettings={defaultUserSettings}
                      handleResetAnimation={handleResetAnimation}
                      toggleMultiSort={toggleMultiSort}
                      multiColumnSort={multiColumnSort}
                      setExportProgress={setExportProgress}
                      exportWithStyles={false}
                      // Multiple saved views feature props
                      useMultipleViews={true}
                      currentViewName={currentViewNameForUI}
                      onViewLoaded={handleViewLoaded}
                      getGridFilterDefinition={getGridFilterDefinition}
                    >
                      {actions => {
                        const { isOneRowSelected, ColumnsAction } = actions;
                        return (
                          <>
                            <MobileView>
                              <ModuleMenuBar
                                {...actions}
                                showDateFilterDropdown
                                showCollapse
                                moduleRefetch={refetch}
                                ColumnsAction={ColumnsAction}
                                changeDisplayMenu={changeDisplayMenu}
                                displayMenu={displayMenu}
                                onGroupCollapsedChanged={onGroupCollapsedChanged}
                                filters={filters}
                                saveStatus={saveStatus}
                                onFiltersChanged={onFiltersChanged}
                                menuButtonRef={menuButtonRef}
                                clearInternalFilters={clearInternalFilters}
                              />
                            </MobileView>
                            <div
                              ref={menuRef}
                              style={{
                                justifyContent: 'space-between',
                                padding: '0 10px',
                                display: 'flex',
                                flexDirection: 'row',
                                minHeight: 'auto',
                                ...(isMobile && {
                                  position: 'absolute',
                                  bottom: 0,
                                  boxShadow:
                                    '0px -4px 4px 0px #ACACAC40',
                                  zIndex: 100,
                                  background: '#fff',
                                  borderRadius: '8px 8px 0px 0px',
                                  width: '100%',
                                  display: isOneRowSelected || displayMenu
                                    ? 'flex'
                                    : 'none',
                                  justifyContent: 'center',
                                  height: (displayMenu && '189px') || (isOneRowSelected && '150px')
                                }),
                              }}
                            >
                              <Col>
                                <ActionsOrganizer
                                  {...actions}
                                  minHeight="80px"
                                  onFiltersChanged={onFiltersChanged}
                                  filters={filters}
                                  isRep={isAccountRep(userSettings.accountType)}
                                  saveStatus={saveStatus}
                                  repCode={userSettings.repCode}
                                  onGroupCollapsedChanged={onGroupCollapsedChanged}
                                  clearInternalFilters={clearInternalFilters}
                                  displayRowMenu={isOneRowSelected}
                                  displayMenu={displayMenu}
                                  toggleMultiSort={toggleMultiSort}
                                  setCollapseLoading={setCollapseLoading}
                                />
                              </Col>
                            </div>
                          </>
                        )
                      }}
                    </ActionsHeader>
                    <div>
                      <GridFlask
                        collection={collection}
                        columnsMapping={columnsMapping}
                        id={moduleName}
                        grid={grid}
                        frozenColumns={frozenColumns}
                        hasAllColumnsHidden={hasAllColumnsHidden}
                        hasDataToDisplay={hasDataToDisplay}
                        onGridInitialized={onGridInitialized}
                        onInternalGridFiltersChanged={onInternalGridFiltersChanged}
                        onClearSelection={onClearSelection}
                        onRowSelected={onRowSelected}
                        onGroupPanelChanged={onGroupPanelChanged}
                        onColSorted={onColSorted}
                        onColResized={onColResized}
                        onColDragged={onColDragged}
                        onColFreezed={onColFreezed}
                        onRowFreezed={onRowFreezed}
                        status={status}
                        formatters={formatters}
                        showFrozenBars
                        showGroupPanel
                        active={props.active}
                        groups={groups}
                        playResetAnimation={playResetAnimation}
                        onFiltersChanged={onFiltersChanged}
                        isRep={isAccountRep(userSettings.accountType)}
                        repCode={userSettings.repCode}
                        autoMTD
                        setFlexGridFilter={setFlexGridFilter}
                        onCurrentItemChanged={onCurrentItemChanged}
                        menuRef={menuRef}
                        isMobile={isMobile}
                        currentItem={currentItem}
                        multiColumnSort={multiColumnSort}
                        exportProgress={exportProgress}
                      />
                    </div>
                  </React.Fragment>
                );
              }}
            </GridFlaskController>
          );
        }}
      </Query>
    </PageContainer>
  );
}

export default Invoices;
